import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { ConfigurationMutationTypes as MutationTypes } from './mutation-types';
import { ConfigurationActionTypes as ActionTypes } from './action-types';
import { ConfigurationModel } from '@/models/configuration-model';

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1],
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>

export interface Actions {
  [ActionTypes.SET_CONFIGURATION]({ commit }: AugmentedActionContext, payload: ConfigurationModel): void;

  [ActionTypes.SET_CONFIG_BAGSIZE]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_BODY]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_EDGE]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_THREAD]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_INSIDE]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_POCKET]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_SIDE]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_FRONT]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_TACK]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_BACK]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_NAMECHAR]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_NAEMTHREAD]({ commit }: AugmentedActionContext, payload: string | null): void;
  [ActionTypes.SET_CONFIG_NONAME]({ commit }: AugmentedActionContext, payload: string | null): void;
}

export const actions: ActionTree<State, RootState> & Actions = {
  [ActionTypes.SET_CONFIGURATION]({ commit }, payload: ConfigurationModel) {
    loadConfig(commit, payload)
  },

  [ActionTypes.SET_CONFIG_BAGSIZE]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_BAGSIZE, payload);
  },
  [ActionTypes.SET_CONFIG_BODY]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_BODY, payload);
  },
  [ActionTypes.SET_CONFIG_EDGE]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_EDGE, payload);
  },
  [ActionTypes.SET_CONFIG_THREAD]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_THREAD, payload);
  },
  [ActionTypes.SET_CONFIG_INSIDE]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_INSIDE, payload);
  },
  [ActionTypes.SET_CONFIG_POCKET]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_POCKET, payload);
  },
  [ActionTypes.SET_CONFIG_SIDE]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_SIDE, payload);
  },
  [ActionTypes.SET_CONFIG_FRONT]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_FRONT, payload);
  },
  [ActionTypes.SET_CONFIG_TACK]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_TACK, payload);
  },
  [ActionTypes.SET_CONFIG_BACK]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_BACK, payload);
  },
  [ActionTypes.SET_CONFIG_NAMECHAR]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_NAMECHAR, payload);
  },
  [ActionTypes.SET_CONFIG_NAEMTHREAD]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_NAEMTHREAD, payload);
  },
  [ActionTypes.SET_CONFIG_NONAME]({ commit }, payload: string | null) {
    commit(MutationTypes.SET_CONFIG_NONAME, payload);
  },
};

async function loadConfig(commit, payload: ConfigurationModel) {
  const startTime = new Date().getTime();
  const delay = 25;
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_BODY, payload.body);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_EDGE, payload.edge);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_THREAD, payload.thread);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_INSIDE, payload.inside);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_POCKET, payload.pocket);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_SIDE, payload.side);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_FRONT, payload.front);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_TACK, payload.tack);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_BACK, payload.back);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_NAMECHAR, payload.namechar);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_NAEMTHREAD, payload.namethread);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_NONAME, payload.noName);
  await wait(delay)
  commit(MutationTypes.SET_CONFIG_BAGSIZE, payload.bagsize);

  const endTime = new Date().getTime();
  console.log(`load config took ${(endTime - startTime)} ms`);
  commit(MutationTypes.CONFIG_LOADED, true)
}

function wait(n: number) {
  return new Promise(function (resolve) {
    setTimeout(resolve, n);
  });
}
